import { useEffect, useRef, useState } from "react";
import Header from "@/components/landing/layout/Header";
import Footer from "@/components/landing/layout/Footer";
import { Link } from "react-router-dom";
import { Image, Headphones, RefreshCw, ChevronDown, Plus } from "lucide-react";
import Lenis from "lenis";
import { useGSAP } from "@/hooks/useGSAP";

type Feature = {
  icon: React.ElementType;
  title: string;
  desc?: string;
};

type FaqItem = {
  q: string;
  a: string;
};

const FEATURES: Feature[] = [
  { icon: Image, title: "Accidental damage", desc: "" },
  { icon: Headphones, title: "On-Demand Support", desc: "" },
  { icon: RefreshCw, title: "Replacement", desc: "" },
];

const FAQS: FaqItem[] = [
  {
    q: "What is the beach company Care Plan?",
    a: "The beach company Care provides accidental damage coverage, extended warranty coverage and peace of mind. We get that accidents happen. That’s why with the beach company Care, we’ll provide you with a replacement product should you need one. The enhanced coverage begins at the time of purchase, and covers until the end of the beach company plan term length. See Terms and Conditions for additional details.",
  },
  {
    q: "Why should I add the beach company care plan?",
    a: "Protega provides coverage for accidental damage not covered by our manufacturer’s warranty starting on day one. After your manufacturer's warranty expires, you’re still covered for Rips & Tears for the remainder of your Protega coverage term.",
  },
  {
    q: "What happens if I return my product?",
    a: "If you return your product, the beach company returns the amount you paid for your product protection. Depending on how much time has passed since your purchase, you may receive a prorated refund, less any paid service.",
  },
  {
    q: "I haven’t received a refund for my protection plan, even though I returned my product.",
    a: "The beach company handles all transactions for product protection sales and refunds. For assistance with refunds, contact our Customer Care.",
  },
  {
    q: "Is this plan the same as warranty or insurance?",
    a: "No. It is an optional protection plan for accidental events, separate from any manufacturer warranty or insurance.",
  },
];

const AccordionItem: React.FC<{
  item: FaqItem;
  index: number;
  isOpen: boolean;
  onToggle: (i: number) => void;
}> = ({ item, index, isOpen, onToggle }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [maxH, setMaxH] = useState<string>("0px");

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (isOpen) {
      setMaxH(`${el.scrollHeight}px`);
      const t = setTimeout(() => setMaxH("none"), 300);
      return () => clearTimeout(t);
    } else {
      if (el.scrollHeight && maxH === "none") {
        setMaxH(`${el.scrollHeight}px`);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setMaxH("0px"));
        });
      } else {
        setMaxH("0px");
      }
    }
  }, [isOpen]);

  return (
    <div className="tw-border-b tw-text-left tw-border-gray-200">
      <button
        onClick={() => onToggle(index)}
        aria-expanded={isOpen}
        aria-controls={`faq-${index}`}
        className="tw-w-full tw-flex tw-justify-between tw-items-start tw-gap-4 tw-px-8 tw-py-6"
      >
        <div className="tw-text-left">
          <div className="tw-text-lg tw-font-medium">{item.q}</div>
        </div>
        <ChevronDown
          className={`tw-h-5 tw-w-5 tw-transition-transform tw-duration-300 ${
            isOpen ? "tw-rotate-180" : "tw-rotate-0"
          }`}
        />
      </button>

      <div
        id={`faq-${index}`}
        ref={ref}
        style={{
          maxHeight: maxH === "none" ? undefined : maxH,
          overflow: "hidden",
          transition: "max-height 300ms ease, opacity 200ms ease",
          opacity: isOpen ? 1 : 0,
        }}
        className="tw-px-6 tw-pb-4 tw-text-md tw-text-gray-700"
        aria-hidden={!isOpen}
      >
        <div className="tw-pt-1">{item.a}</div>
      </div>
    </div>
  );
};

const BeachCompanyCare: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleIndex = (i: number) =>
    setOpenIndex((prev) => (prev === i ? null : i));

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const lenis = new Lenis({
      duration: 1.6,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: "vertical",
      smoothWheel: true,
      touchMultiplier: 2,
    });

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  useGSAP();

  return (
    <div className="tw-min-h-screen tw-bg-black tw-text-white tw-font-mori">
      <Header />
      <main className="tw-relative">
        <div className="tw-h-20 lg:tw-h-24" />

        {/* HERO: rounded banner with white CTA card */}
        <section className="tw-relative tw-w-[95%] tw-mx-auto">
          <div className="tw-relative tw-overflow-hidden tw-flex tw-items-end tw-justify-start  tw-rounded-xl tw-h-[80vh]">
            <img
              src="/clients/thebeachcompany/hero-banner.webp"
              alt="Hero banner"
              className="tw-object-cover tw-absolute -tw-z-1 tw-w-full tw-h-full"
            />
            {/* white CTA card (left) */}
            <div className="tw-bg-white tw-text-black tw-relative tw-rounded-lg tw-shadow-md tw-px-5 md:tw-px-14 tw-py-10 md:tw-m-20 tw-m-5   md:tw-w-[450px]">
              <h1 className="tw-text-3xl md:tw-text-3xl tw-font-semibold">
                Enjoy peace of mind!
              </h1>
              <p className="tw-mt-2 tw-text-lg tw-text-gray-700">
                Simple, stress-free product protection for the stuff you love.
              </p>
              <a
                href="https://www.thebeachcompany.in"
                target="_blank"
                rel="noreferrer"
                className="tw-inline-block tw-mt-4 tw-text-md tw-font-medium tw-px-4 tw-py-2 tw-bg-[#00AEEF] tw-text-white tw-rounded-sm hover:tw-opacity-90 tw-transition-opacity"
              >
                Shop Now
              </a>
            </div>
          </div>
        </section>

        {/* Logos row */}
        <section className="tw-bg-white tw-py-20">
          <div className="tw-w-[95%] tw-mx-auto tw-flex tw-justify-center tw-items-center tw-gap-8">
            <img
              src="/clients/thebeachcompany/logo-beachco.png"
              alt="beach company"
              className="md:tw-w-24 tw-w-[30%]"
            />
            <Plus className="tw-text-black" />
            <img
              src="/icons/logo.png"
              alt="protega"
              className="md:tw-w-24 tw-w-[30%]"
            />
          </div>
        </section>

        {/* Blue features strip */}
        <section className="tw-bg-[#00AEEF] tw-text-black tw-py-10">
          <div className="md:tw-w-[65%] tw-mx-auto tw-text-center">
            <h2 className="tw-text-xl md:tw-text-3xl tw-font-semibold tw-mb-6">
              Enjoy peace of mind with the beach company Care Plan.
            </h2>

            <div className="tw-flex md:tw-justify-between tw-justify-center tw-gap-10 tw-flex-wrap tw-mt-6">
              {FEATURES.map((f, i) => (
                <div
                  key={i}
                  className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-max-w-[220px]"
                >
                  <div className="tw-bg-black tw-rounded-full tw-p-4 tw-w-16 tw-h-16 tw-flex tw-items-center tw-justify-center">
                    <f.icon className="tw-w-6 tw-h-6 tw-text-white" />
                  </div>
                  <div className="tw-text-lg tw-font-medium">{f.title}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Simple care strip */}
        <section className="tw-bg-white tw-py-36  tw-border-b tw-border-gray-200 tw-text-black">
          <div className="tw-w-[95%] tw-mx-auto tw-text-center">
            <h3 className="tw-text-4xl tw-font-semibold tw-mb-4">
              Simple care. Strong coverage.
            </h3>
            <p className="tw-text-lg tw-text-justify tw-px-5 tw-text-gray-700">
              ✓ Accidental Damage Coverage: from the date of plan purchase, it
              covers accidental damage from drops, spills, and pets.
            </p>
          </div>
        </section>

        {/* How it works (blue background) */}
        <section className="tw-py-10 tw-relative tw-bg-[#00AEEF] md:tw-min-h-[90vh] tw-overflow-hidden tw-text-black">
          <img
            src="/LovePangolinFAQ/HowItWorks.png"
            className="tw-absolute lg:tw-h-[90%] md:tw-block tw-hidden lg:tw-top-5 lg:tw-right-20 -tw-right-10 lg:tw-w-8/12 tw-object-top tw-object-cover"
            alt="Image"
          />
          <div className="md:tw-w-4/5  tw-mx-auto tw-relative tw-z-10">
            <h1 className="tw-text-5xl tw-text-center tw-mb-10 tw-font-semibold">
              How it works
            </h1>
            <div className="md:tw-w-2/5 md:tw-px-0 tw-px-10 tw-flex tw-flex-col tw-gap-5 tw-items-start">
              <h2 className="tw-text-3xl tw-font-semibold">
                Easy claims, great service
              </h2>
              <p className="tw-text-lg">
                Filing a claim is simple. They’ll process your claim in minutes
                and once approved, they’ll mail you details on your replacement.
              </p>
              <button className="tw-border hover:tw-bg-black hover:tw-text-white tw-transition-all tw-border-black tw-p-8 tw-py-3">
                Start a Claim
              </button>
            </div>
          </div>
        </section>

        {/* How to buy */}
        <section className="tw-py-16 tw-w-[95%] tw-mx-auto tw-text-center tw-bg-white tw-text-black">
          <h3 className="tw-text-2xl tw-font-semibold tw-mb-4">
            How to buy the beach company Care Plan?
          </h3>
          <p className="tw-text-sm tw-text-gray-700 tw-max-w-3xl tw-mx-auto">
            You can add the beach company Care to your cart when you buy beach
            company products on{" "}
            <a
              className="tw-text-blue-600 hover:tw-underline"
              href="https://www.thebeachcompany.in"
              target="_blank"
              rel="noreferrer"
            >
              https://www.thebeachcompany.in/
            </a>
            . Product owners eligible for the beach company Care Plan can find
            an offer on the beach company website.
          </p>
        </section>

        {/* Large pool image */}
        <section className="tw-w-full">
          <img
            src="/clients/thebeachcompany/pool-large.webp"
            alt="pool"
            className="tw-w-full tw-h-[380px] tw-object-cover"
          />
        </section>

        {/* FAQ accordion */}
        <section className="tw-w-full tw-pt-12 tw-pb-16 tw-text-center tw-bg-white tw-text-black">
          <div className="tw-w-[95%] tw-mx-auto">
            <h3 className="tw-text-3xl tw-font-semibold tw-mb-8">
              Frequently asked questions
            </h3>
            <div className="tw-max-w-3xl tw-mx-auto tw-border tw-border-gray-200 tw-rounded tw-shadow-sm">
              {FAQS.map((item, idx) => (
                <AccordionItem
                  key={idx}
                  item={item}
                  index={idx}
                  isOpen={openIndex === idx}
                  onToggle={toggleIndex}
                />
              ))}
            </div>

            <div className="tw-mt-12">
              <Link
                to={"/thebeachcompany/tnc"}
                className="tw-text-lg tw-text-gray-600 hover:tw-underline"
              >
                Terms and Conditions
              </Link>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </div>
  );
};

export default BeachCompanyCare;
